import React, { useState } from "react"
import {
  Form,
  Row,
  Col,
  Button,
  Popover,
  OverlayTrigger,
  Table,
} from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"

import { formatCurrency } from "./utils"

const ModelInput = (props) => {
  const [validated, setValidated] = useState(false)

  const { onSubmit } = props

  const handleSubmit = (event) => {
    const form = event.currentTarget

    event.preventDefault()
    event.stopPropagation()

    let validity = form.checkValidity()
    setValidated(true)
    if (validity === false) {
      return
    }

    const data = {
      desiredMrr: parseInt(form.elements.desiredMrr.value),
      conversion: parseFloat(form.elements.conversion.value),
      arpa: parseInt(form.elements.arpa.value),
      newTrialsMonthly: parseInt(form.elements.newTrialsMonthly.value),
      monthlyTrialsGrowth: parseFloat(form.elements.monthlyTrialsGrowth.value),
    }

    onSubmit(data)
  }

  const mrrGoalInfo = (
    <Popover>
      <Popover.Title as="h3">New MRR goal</Popover.Title>
      <Popover.Content>MRR goal that you want to achieve.</Popover.Content>
    </Popover>
  )

  const conversionInfo = (
    <Popover>
      <Popover.Title as="h3">Conversion</Popover.Title>
      <Popover.Content>
        Enter a conversion from trial or demo account in your system to paying
        customer.
      </Popover.Content>
    </Popover>
  )

  const arpaInfo = (
    <Popover>
      <Popover.Title as="h3">Average revenue per account</Popover.Title>
      <Popover.Content>
        On average what revenue your expect to receive per account.
      </Popover.Content>
    </Popover>
  )

  const newTrialsMonthlyInfo = (
    <Popover>
      <Popover.Title as="h3">New trials / demo you have monthly</Popover.Title>
      <Popover.Content>
        How many new trial or demo accounts created in your system per month?
      </Popover.Content>
    </Popover>
  )

  const monthlyTrialsGrowthInfo = (
    <Popover>
      <Popover.Title as="h3">
        Monthly growth of trial / demo accounts
      </Popover.Title>
      <Popover.Content>
        Do you bring more and more trials each month? If yes what is month over
        month growth in percents.
      </Popover.Content>
    </Popover>
  )

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      <Form.Group as={Row} controlId="churnForm.desiredMrr">
        <Form.Label column sm="8">
          New MRR goal
          <OverlayTrigger trigger="hover" placement="top" overlay={mrrGoalInfo}>
            <FontAwesomeIcon icon={faInfoCircle} className="ml-1" />
          </OverlayTrigger>
        </Form.Label>
        <Col sm="4">
          <Form.Control
            required
            name="desiredMrr"
            type="numeric"
            placeholder="10000"
            defaultValue="10000"
          />
          <Form.Control.Feedback type="invalid">
            MRR goal Ex. 10000
          </Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="churnForm.conversion">
        <Form.Label column sm="8">
          Converstion from trial/demo to paying customer %
          <OverlayTrigger
            trigger="hover"
            placement="top"
            overlay={conversionInfo}
          >
            <FontAwesomeIcon icon={faInfoCircle} className="ml-1" />
          </OverlayTrigger>
        </Form.Label>
        <Col sm="4">
          <Form.Control
            required
            name="conversion"
            type="number"
            min="0"
            max="100"
            step="0.1"
            placeholder="5"
            defaultValue="5"
          />
          <Form.Control.Feedback type="invalid">
            Conversion from trial/demo to paying customer. Percent. Ex. 5
          </Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="churnForm.arpa">
        <Form.Label column sm="8">
          Average revenue per account
          <OverlayTrigger trigger="hover" placement="top" overlay={arpaInfo}>
            <FontAwesomeIcon icon={faInfoCircle} className="ml-1" />
          </OverlayTrigger>
        </Form.Label>
        <Col sm="4">
          <Form.Control
            required
            name="arpa"
            type="numeric"
            placeholder="250"
            defaultValue="250"
          />
          <Form.Control.Feedback type="invalid">
            Ex. 250 dollarts per account
          </Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="churnForm.newTrialsMonthly">
        <Form.Label column sm="8">
          How many new trials/demos per month do you have
          <OverlayTrigger
            trigger="hover"
            placement="top"
            overlay={newTrialsMonthlyInfo}
          >
            <FontAwesomeIcon icon={faInfoCircle} className="ml-1" />
          </OverlayTrigger>
        </Form.Label>
        <Col sm="4">
          <Form.Control
            required
            name="newTrialsMonthly"
            type="numeric"
            placeholder="10"
            defaultValue="10"
          />
          <Form.Control.Feedback type="invalid">
            New trials/demos per month. Ex. 50
          </Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="churnForm.monthlyTrialsGrowth">
        <Form.Label column sm="8">
          Monthly growth in new trials/demos %
          <OverlayTrigger
            trigger="hover"
            placement="top"
            overlay={monthlyTrialsGrowthInfo}
          >
            <FontAwesomeIcon icon={faInfoCircle} className="ml-1" />
          </OverlayTrigger>
        </Form.Label>
        <Col sm="4">
          <Form.Control
            required
            name="monthlyTrialsGrowth"
            type="number"
            min="0"
            step="0.1"
            placeholder="10"
            defaultValue="10"
          />
          <Form.Control.Feedback type="invalid">
            Month over month growth. Percent. Ex. 4.5
          </Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Button variant="primary" type="submit">
        Calculate
      </Button>
    </Form>
  )
}

const ImpactChart = (props) => {
  const { data } = props

  const formateer = (value, name, props) => {
    return [formatCurrency(value), name]
  }

  return (
    <ResponsiveContainer
      width="90%"
      height={400}
      margin={{ top: 20, bottom: 20 }}
    >
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" padding={{ left: 30, right: 30 }} />
        <YAxis />
        <Tooltip formatter={formateer} />
        <Legend />
        <Line
          name="MRR"
          type="monotone"
          dataKey="mrr"
          stroke="#8884d8"
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

const ModelDetailsTable = (props) => {
  const { trials, mrr, requiredNumber } = props

  return (
    <>
      {mrr.length > 0 && (
        <p>
          You need to generate{" "}
          <big>
            <b>{requiredNumber}</b>
          </big>{" "}
          demos/trials to achieve desired growth. It will take you{" "}
          <big>
            <b>{`${mrr.length} months`}</b>
          </big>{" "}
          with the current growth of new trials/demos
        </p>
      )}
      <Table responsive>
        <thead>
          <tr>
            <th>Month</th>
            {mrr.map((value, i) => (
              <th>{i + 1}</th>
            ))}
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>New trials/demos</th>
            {trials.map((value, i) => (
              <td>{value}</td>
            ))}
          </tr>
          <tr>
            <th>MRR</th>
            {mrr.map((value, i) => (
              <td>{formatCurrency(value)}</td>
            ))}
          </tr>
        </tbody>
      </Table>
    </>
  )
}

class GrowthEstimationImpact extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      requiredNumber: 0,
      trials: [],
      mrr: [],
    }
  }

  calculateImpact = (data) => {
    const {
      desiredMrr,
      conversion,
      arpa,
      newTrialsMonthly,
      monthlyTrialsGrowth,
    } = data

    const requiredNumber = Math.round(desiredMrr / arpa / (conversion / 100))

    let trials = [newTrialsMonthly]
    let mrr = [newTrialsMonthly * (conversion / 100) * arpa]
    let step = 0
    while (mrr[mrr.length - 1] < desiredMrr) {
      trials.push(
        Math.round(trials[step] + (trials[step] * monthlyTrialsGrowth) / 100)
      )
      mrr.push(mrr[step] + trials[step + 1] * (conversion / 100) * arpa)

      step++
    }

    this.setState({ requiredNumber, trials, mrr })
  }

  componentDidMount = () => {
    this.calculateImpact({
      desiredMrr: 10000,
      conversion: 5,
      arpa: 250,
      newTrialsMonthly: 10,
      monthlyTrialsGrowth: 10,
    })
  }

  convertToLineChartData = (mrr) => {
    let data = []
    for (let step = 0; step < mrr.length; step++) {
      data.push({
        month: `Month ${step + 1}`,
        mrr: mrr[step],
      })
    }

    return data
  }

  render() {
    const { requiredNumber, mrr, trials } = this.state

    return (
      <>
        <Row className="justify-content-md-center mb-5">
          <Col className="justify-content-md-center">
            <h1 className="text-center mb-3">
              <small>Growth estimation</small>
            </h1>
            <p class="lead">
              You can estimate your growth by knowing just 4 numbers. Use the
              calculator below to understand how many trials or demos you need
              to get in order to achieve your MRR goal.{" "}
              <a href="https://write.as/probe/4-numbers-to-start-estimating-your-growth">
                Read our blog post
              </a>{" "}
              that walks you through estimation of the startup growth.
            </p>
          </Col>
        </Row>
        <Row className="justify-content-md-center">
          <Col
            className="justify-content-md-center sm-mb-5"
            sm="12"
            md="12"
            lg="5"
          >
            <ModelInput onSubmit={this.calculateImpact} />
          </Col>
          <Col sm="12" md="12" lg="7">
            <ImpactChart data={this.convertToLineChartData(mrr)} />
          </Col>
        </Row>
        <Row className="justify-content-md-center mt-5">
          <Col>
            <ModelDetailsTable
              mrr={mrr}
              trials={trials}
              requiredNumber={requiredNumber}
            />
          </Col>
        </Row>
      </>
    )
  }
}

export default GrowthEstimationImpact
